'use client';

import { useState } from 'react';
import HTMLParser from 'html-react-parser';
import { RichTextModel } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerFooter } from '@/components/ui/drawer';

// ─── InformativeSheet ─────────────────────────────────────────────────────────

export interface InformativeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Header label centered above the body. Defaults to "Information". */
  label?: string;
  /** Large heading inside the sheet body. */
  title?: string;
  /** Rich-text description rendered below the heading. */
  description?: RichTextModel;
  /** Plain-text description rendered below the heading (used when CMS field is a string, not RichText). */
  descriptionText?: string;
  /** Primary CTA label. */
  confirmLabel?: string;
  /** Dismiss link label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Called when the user taps the primary CTA. May return a Promise; the button shows loading until it resolves. */
  onConfirm?: () => void | Promise<void>;
}

export function InformativeSheet({
  open,
  onOpenChange,
  label,
  title,
  description,
  descriptionText,
  confirmLabel,
  cancelLabel,
  onConfirm,
}: InformativeSheetProps) {
  const t = useClientTranslation();
  const _label = label ?? t('App.Labels.Information');
  const _cancelLabel = cancelLabel ?? t('App.Labels.Cancel');
  const [confirming, setConfirming] = useState(false);

  async function handleConfirm() {
    setConfirming(true);
    await onConfirm?.();
    setConfirming(false);
    onOpenChange(false);
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="pt-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.75">
          <div className="w-11" />
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-center text-[14px] tracking-[0.5px] uppercase">
            {_label}
          </p>
          <div className="w-11" />
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 px-4 pt-6 pb-10">
          {title && (
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[24px] leading-none font-semibold">
              {title}
            </p>
          )}
          {description?.markup && (
            <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] leading-none tracking-[0.25px]">
              {HTMLParser(description.markup)}
            </div>
          )}
          {!description?.markup && descriptionText && (
            <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] leading-none tracking-[0.25px]">
              {HTMLParser(descriptionText)}
            </div>
          )}
        </div>

        {/* Footer */}
        <DrawerFooter className="gap-1">
          {confirmLabel && (
            <Button
              variant="primary-solid"
              palette="dark"
              size="md"
              loading={confirming}
              disabled={confirming}
              onClick={handleConfirm}
              className="w-full"
            >
              {confirmLabel}
            </Button>
          )}
          <Button
            variant="tertiary-link"
            palette="dark"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            {_cancelLabel}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
